import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { buildApprovalSubmissionFeedback } from "@/features/approvals/approval-utils";
import type { PermissionCode } from "@/features/auth/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";
import { createLogger } from "@/lib/logger";
import {
    useUserPresenceLookup,
    type UserPresenceConnectionState,
    type UserPresenceSnapshot,
} from "@/lib/realtime/userPresence";
import {
    ArrowLeftIcon,
    EnvelopeIcon,
    PhoneIcon,
    IdentificationIcon,
    CheckCircleIcon,
    XCircleIcon,
    CalendarIcon,
    ClockIcon,
    UserCircleIcon,
    BuildingOfficeIcon,
    BriefcaseIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    TicketIcon,
    TruckIcon,
    WalletIcon,
    ArrowUpIcon,
    ArrowDownIcon,
    MapPinIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    StarIcon,
    NoSymbolIcon,
    PowerIcon,
    TrashIcon,
    XMarkIcon,
    PaperAirplaneIcon,
    ShieldExclamationIcon,
    LockClosedIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import {
    getRiderDetails,
    getRiderBookings,
    getRiderTrips,
    getRiderWallet,
    activateRider,
    reactivateRider,
    deactivateRider,
    suspendRider,
    banRider,
} from "../api/ridersApi";
import { cancelBooking, refundBooking, cancelTrip, forceCompleteTrip } from "@/features/ride-bookings-trips/api/rideBookingsTripsApi";
import { creditWallet, debitWallet, freezeWallet } from "@/features/wallet-payments/api/walletPaymentsApi";
import { sendPushNotificationToUser } from "@/features/notifications/api/messagingApi";
import { getUserRiskScore } from "@/features/fraud-safety/api/fraudSafetyApi";
import type {
    RiderDetails,
    RiderBooking,
    RiderTrip,
    RiderWalletTransaction,
    RiderTripSummary,
    RiderWalletSummary,
} from "../types";

// ============================================================================
// Module Logger
// ============================================================================

const log = createLogger("RiderDetails");

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatShortDate = (dateString: string | null) => {
    if (!dateString) return "\u2014";
    return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
};

const formatCurrency = (amount: number | null, currency = "NGN") => {
    if (amount === null || amount === undefined) return "\u2014";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency }).format(amount);
};

const formatEnumLabel = (value: string | null) => {
    if (!value) return "\u2014";
    return value
        .toLowerCase()
        .split("_")
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(" ");
};

const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "\u2014";

    const timestamp = new Date(dateString).getTime();

    if (Number.isNaN(timestamp)) {
        return "\u2014";
    }

    const elapsedMs = Math.max(Date.now() - timestamp, 0);
    const elapsedMinutes = Math.floor(elapsedMs / 60_000);

    if (elapsedMinutes < 1) return "Just now";
    if (elapsedMinutes < 60) {
        return `${elapsedMinutes} min${elapsedMinutes === 1 ? "" : "s"} ago`;
    }

    const elapsedHours = Math.floor(elapsedMinutes / 60);

    if (elapsedHours < 24) {
        return `${elapsedHours} hour${elapsedHours === 1 ? "" : "s"} ago`;
    }

    const elapsedDays = Math.floor(elapsedHours / 24);

    if (elapsedDays < 7) {
        return `${elapsedDays} day${elapsedDays === 1 ? "" : "s"} ago`;
    }

    return formatDate(dateString);
};

const formatCoordinates = (latitude: number | null, longitude: number | null) => {
    if (latitude === null || longitude === null) return "Unavailable";
    return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
};

const isExternalResource = (value: string | null) => {
    if (!value) return false;
    return /^(https?:)?\/\//i.test(value);
};

const NOTIFICATION_PREFERENCE_LABELS = {
    promotionAndOffers: "Promotions & Offers",
    appUpdatesAndNews: "App Updates & News",
    safetyAlerts: "Safety Alerts",
    rideUpdates: "Ride Updates",
    paymentAndWallets: "Payments & Wallets",
} as const;

// ============================================================================
// Shared UI Components
// ============================================================================

const StatusBadge = ({ status }: { status: string }) => {
    const statusStyles: Record<string, string> = {
        ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        INACTIVE: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
        SUSPENDED: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        DELETED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        BANNED: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        COMPLETED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        CANCELLED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        CONFIRMED: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        PENDING: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
        PAID: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        UNPAID: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        IN_PROGRESS: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        SUCCESS: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        FAILED: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        CREDIT: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
        DEBIT: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400",
        FROZEN: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    };
    return (
        <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[status] || "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"}`}
        >
            {status?.replace(/_/g, " ")}
        </span>
    );
};

const VerificationBadge = ({ verified, label }: { verified: boolean; label: string }) => (
    <div className="flex items-center gap-2">
        {verified ? (
            <CheckCircleIcon className="h-5 w-5 text-emerald-500" />
        ) : (
            <XCircleIcon className="h-5 w-5 text-slate-400" />
        )}
        <span
            className={`text-sm font-medium ${verified ? "text-emerald-700 dark:text-emerald-400" : "text-slate-500 dark:text-slate-400"}`}
        >
            {label}
        </span>
    </div>
);

const ProfileSectionCard = ({
    title,
    className = "",
    children,
}: {
    title: string;
    className?: string;
    children: React.ReactNode;
}) => (
    <div
        className={`overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 ${className}`.trim()}
    >
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
        </div>
        {children}
    </div>
);

const ProfileDetail = ({
    icon: Icon,
    label,
    value,
    valueClassName = "",
}: {
    icon?: React.ElementType;
    label: string;
    value: React.ReactNode;
    valueClassName?: string;
}) => (
    <div className={`flex items-start ${Icon ? "gap-4" : ""}`}>
        {Icon ? <Icon className="mt-0.5 h-5 w-5 shrink-0 text-slate-400" /> : null}
        <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{label}</p>
            <div
                className={`mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100 ${valueClassName}`.trim()}
            >
                {value}
            </div>
        </div>
    </div>
);

const PresencePanel = ({
    presence,
    connectionState,
    error,
    lastUpdatedAt,
    onRefresh,
}: {
    presence: UserPresenceSnapshot | null;
    connectionState: UserPresenceConnectionState;
    error: string | null;
    lastUpdatedAt: string | null;
    onRefresh: () => void;
}) => {
    const isOnline =
        connectionState === "connected" && presence?.onlineStatus === "ONLINE";
    const isConnecting = connectionState === "connecting";
    const statusTone = isOnline
        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
        : isConnecting
          ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";

    const summary = isOnline
        ? "Present and live"
        : presence
          ? presence.onlineStatus === "ONLINE"
            ? "Last reported online"
            : "Not currently active"
          : isConnecting
            ? "Checking live presence..."
            : "Presence unavailable";

    return (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col gap-4 border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        User Presence
                    </h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        Realtime mobile presence while this page stays open.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${statusTone}`}>
                        {isOnline ? (
                            <span className="relative flex h-3 w-3">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
                            </span>
                        ) : (
                            <span
                                className={`inline-flex h-3 w-3 rounded-full ${isConnecting ? "bg-amber-500" : "bg-slate-400 dark:bg-slate-500"}`}
                            />
                        )}
                        <span>{isOnline ? "Live" : presence?.onlineStatus ? formatEnumLabel(presence.onlineStatus) : isConnecting ? "Checking" : "Unavailable"}</span>
                    </div>
                    <button
                        onClick={onRefresh}
                        disabled={connectionState !== "connected"}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-6 bg-gradient-to-r from-emerald-500/[0.04] via-transparent to-transparent p-6 md:grid-cols-4">
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Presence
                    </p>
                    <p
                        className={`mt-2 text-sm font-semibold ${
                            isOnline
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-slate-900 dark:text-slate-100"
                        }`}
                    >
                        {summary}
                    </p>
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        App State
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {isOnline ? formatEnumLabel(presence?.appState ?? null) : "\u2014"}
                    </p>
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Last Seen
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {isOnline
                            ? "Live now"
                            : presence?.lastSeenAt
                              ? formatDate(presence.lastSeenAt)
                              : "\u2014"}
                    </p>
                    {!isOnline && presence?.lastSeenAt && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            {formatRelativeTime(presence.lastSeenAt)}
                        </p>
                    )}
                </div>
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Location
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {formatCoordinates(presence?.latitude ?? null, presence?.longitude ?? null)}
                    </p>
                    {presence?.latitude !== null && presence?.longitude !== null && (
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            Latest mobile coordinates
                        </p>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-2 border-t border-slate-200 px-6 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
                <span>
                    {error
                        ? error
                        : lastUpdatedAt
                          ? `Last refreshed ${formatRelativeTime(lastUpdatedAt)}`
                          : "Waiting for first presence reply."}
                </span>
                <span>
                    Socket: {formatEnumLabel(connectionState)}
                    {presence?.role ? ` · ${formatEnumLabel(presence.role)}` : ""}
                </span>
            </div>
        </div>
    );
};

const Pagination = ({
    currentPage,
    totalPages,
    totalElements,
    onPageChange,
}: {
    currentPage: number;
    totalPages: number;
    totalElements: number;
    onPageChange: (page: number) => void;
}) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-800">
            <p className="text-sm text-slate-600 dark:text-slate-400">
                Page {currentPage + 1} of {totalPages} &middot; {totalElements} total
            </p>
            <div className="flex gap-2">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                    <ChevronLeftIcon className="h-4 w-4" /> Prev
                </button>
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                    Next <ChevronRightIcon className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
};

const EmptyState = ({
    icon: Icon,
    title,
    subtitle,
}: {
    icon: React.ElementType;
    title: string;
    subtitle: string;
}) => (
    <div className="flex h-48 flex-col items-center justify-center text-center">
        <Icon className="mb-3 h-10 w-10 text-slate-300 dark:text-slate-600" />
        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{title}</p>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{subtitle}</p>
    </div>
);

const LoadingSpinner = () => (
    <div className="flex h-48 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
    </div>
);

const ErrorState = ({ message }: { message: string }) => (
    <div className="flex h-48 items-center justify-center text-rose-500">
        <p className="text-sm">{message}</p>
    </div>
);

const InlineError = ({ message }: { message: string }) =>
    message ? (
        <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{message}</span>
        </div>
    ) : null;

const InlineSuccess = ({ message }: { message: string }) =>
    message ? (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{message}</span>
        </div>
    ) : null;

const RatingStars = ({ rating }: { rating: number | null }) => {
    if (rating === null || rating === undefined)
        return <span className="text-xs text-slate-400">No rating</span>;
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) =>
                star <= Math.round(rating) ? (
                    <StarSolid key={star} className="h-4 w-4 text-amber-400" />
                ) : (
                    <StarIcon key={star} className="h-4 w-4 text-slate-300 dark:text-slate-600" />
                )
            )}
            <span className="ml-1 text-xs font-medium text-slate-600 dark:text-slate-400">
                {rating.toFixed(1)}
            </span>
        </div>
    );
};

// ============================================================================
// Modal Helpers
// ============================================================================

const ModalOverlay = ({
    children,
    onClose,
}: {
    children: React.ReactNode;
    onClose: () => void;
}) => (
    <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
    >
        {children}
    </div>
);

const ModalCard = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-800">
        {children}
    </div>
);

// ============================================================================
// Tab Config
// ============================================================================

type TabKey = "profile" | "bookings" | "trips" | "wallet" | "fraud";

const TABS: {
    key: TabKey;
    label: string;
    icon: React.ElementType;
    permissions?: readonly PermissionCode[];
}[] = [
    { key: "profile", label: "Profile", icon: UserCircleIcon },
    { key: "bookings", label: "Bookings", icon: TicketIcon, permissions: SECTION_PERMISSIONS.RIDER_BOOKINGS },
    { key: "trips", label: "Trips", icon: TruckIcon, permissions: SECTION_PERMISSIONS.RIDER_TRIPS },
    { key: "wallet", label: "Wallet", icon: WalletIcon, permissions: SECTION_PERMISSIONS.RIDER_WALLET },
    { key: "fraud", label: "Fraud & Safety", icon: ShieldExclamationIcon, permissions: SECTION_PERMISSIONS.FRAUD },
];

// ============================================================================
// Profile Tab
// ============================================================================

const ProfileTab = ({ rider }: { rider: RiderDetails }) => {
    const isNinVerified = rider.ninData?.verified ?? rider.ninVerified;
    const isLivenessVerified = rider.livenessChecked;
    const companyName = rider.companyInfo?.companyName ?? rider.companyName;
    const jobTitle = rider.companyInfo?.jobTitle ?? rider.jobTitle;
    const workEmail = rider.companyInfo?.workEmail ?? null;
    const workAddress = rider.companyInfo?.workAddress ?? null;
    const workIdFile = rider.companyInfo?.workIdFile ?? null;
    const ninDisplayValue = rider.ninData?.maskedValue ?? rider.nin ?? "\u2014";
    const notificationEntries = [
        {
            label: NOTIFICATION_PREFERENCE_LABELS.promotionAndOffers,
            channels: rider.notificationPreferences?.promotionAndOffers ?? [],
        },
        {
            label: NOTIFICATION_PREFERENCE_LABELS.appUpdatesAndNews,
            channels: rider.notificationPreferences?.appUpdatesAndNews ?? [],
        },
        {
            label: NOTIFICATION_PREFERENCE_LABELS.safetyAlerts,
            channels: rider.notificationPreferences?.safetyAlerts ?? [],
        },
        {
            label: NOTIFICATION_PREFERENCE_LABELS.rideUpdates,
            channels: rider.notificationPreferences?.rideUpdates ?? [],
        },
        {
            label: NOTIFICATION_PREFERENCE_LABELS.paymentAndWallets,
            channels: rider.notificationPreferences?.paymentAndWallets ?? [],
        },
    ];
    const hasNotificationPreferences = Boolean(rider.notificationPreferences);
    const hasWorkProfile = Boolean(companyName || jobTitle || workEmail || workAddress || workIdFile);
    const completionPercentage = Math.max(0, Math.min(rider.profileCompletionPercentage, 100));

    return (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <ProfileSectionCard title="Personal Details">
                <div className="space-y-4 p-6">
                    <ProfileDetail icon={EnvelopeIcon} label="Email Address" value={rider.email} />
                    <ProfileDetail icon={PhoneIcon} label="Phone Number" value={rider.phoneNumber} />
                    <ProfileDetail
                        icon={UserCircleIcon}
                        label="Role"
                        value={formatEnumLabel(rider.role)}
                    />
                    <ProfileDetail
                        label="Gender"
                        value={rider.gender ? formatEnumLabel(rider.gender) : "Not provided"}
                    />
                    <ProfileDetail
                        icon={MapPinIcon}
                        label="Residential Address"
                        value={rider.residentialAddress || "Not provided"}
                    />
                </div>
            </ProfileSectionCard>

            <ProfileSectionCard title="Verification & Documents">
                <div className="space-y-5 p-6">
                    <div className="grid gap-3 md:grid-cols-2">
                        <VerificationBadge verified={rider.emailVerified} label="Email Verified" />
                        <VerificationBadge verified={rider.phoneVerified} label="Phone Verified" />
                        <VerificationBadge verified={isNinVerified} label="NIN Verified" />
                        <VerificationBadge verified={isLivenessVerified} label="Liveness Verified" />
                    </div>
                    {(rider.nin || rider.ninData?.maskedValue) && (
                        <ProfileDetail
                            icon={IdentificationIcon}
                            label="NIN Number"
                            value={ninDisplayValue}
                            valueClassName="font-mono"
                        />
                    )}
                    {rider.personalId && (
                        isExternalResource(rider.personalId) ? (
                            <ProfileDetail
                                icon={IdentificationIcon}
                                label="Personal ID Document"
                                value={
                                    <a
                                        href={rider.personalId}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium text-primary-600 hover:text-primary-500"
                                    >
                                        View document
                                    </a>
                                }
                            />
                        ) : (
                            <ProfileDetail
                                icon={IdentificationIcon}
                                label="Personal ID"
                                value={rider.personalId}
                            />
                        )
                    )}
                </div>
            </ProfileSectionCard>

            <ProfileSectionCard title="Account Access">
                <div className="space-y-4 p-6">
                    <ProfileDetail label="Account Status" value={<StatusBadge status={rider.status} />} />
                    <ProfileDetail
                        label="Ride Access"
                        value={
                            <span
                                className={
                                    rider.canPerformRides
                                        ? "text-emerald-600 dark:text-emerald-400"
                                        : "text-rose-600 dark:text-rose-400"
                                }
                            >
                                {rider.canPerformRides ? "Allowed" : "Restricted"}
                            </span>
                        }
                    />
                    <ProfileDetail
                        label="Deleted"
                        value={
                            <span
                                className={
                                    rider.deleted
                                        ? "text-rose-600 dark:text-rose-400"
                                        : "text-emerald-600 dark:text-emerald-400"
                                }
                            >
                                {rider.deleted ? "Yes" : "No"}
                            </span>
                        }
                    />
                </div>
            </ProfileSectionCard>

            {hasWorkProfile && (
                <ProfileSectionCard title="Work Profile">
                    <div className="space-y-4 p-6">
                        {companyName && (
                            <ProfileDetail
                                icon={BuildingOfficeIcon}
                                label="Company Name"
                                value={companyName}
                            />
                        )}
                        {jobTitle && (
                            <ProfileDetail
                                icon={BriefcaseIcon}
                                label="Job Title"
                                value={jobTitle}
                            />
                        )}
                        {workEmail && (
                            <ProfileDetail
                                icon={EnvelopeIcon}
                                label="Work Email"
                                value={workEmail}
                            />
                        )}
                        {workAddress && (
                            <ProfileDetail
                                icon={MapPinIcon}
                                label="Work Address"
                                value={workAddress}
                            />
                        )}
                        {workIdFile && (
                            <ProfileDetail
                                label="Work ID Document"
                                value={
                                    <a
                                        href={workIdFile}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-medium text-primary-600 hover:text-primary-500"
                                    >
                                        View document
                                    </a>
                                }
                            />
                        )}
                    </div>
                </ProfileSectionCard>
            )}

            <ProfileSectionCard
                title="Profile Completeness"
                className={hasWorkProfile ? "xl:col-span-2" : "xl:col-span-3"}
            >
                <div className="space-y-5 p-6">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/40">
                        <div className="flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    Current completion
                                </p>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    Based on required and optional rider profile fields
                                </p>
                            </div>
                            <span className="text-2xl font-bold text-primary-600">
                                {completionPercentage}%
                            </span>
                        </div>
                        <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                            <div
                                className="h-full rounded-full bg-primary-500 transition-all"
                                style={{ width: `${completionPercentage}%` }}
                            />
                        </div>
                    </div>
                    {rider.missingRequiredFields.length > 0 ? (
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    Missing Required Fields
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {rider.missingRequiredFields.map((field, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                    >
                                        {field
                                            .replace(/_/g, " ")
                                            .replace(/\b\w/g, (letter) => letter.toUpperCase())}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300">
                            All required profile fields are complete.
                        </div>
                    )}
                    {rider.missingOptionalFields.length > 0 ? (
                        <div>
                            <div className="mb-2 flex items-center gap-2">
                                <InformationCircleIcon className="h-5 w-5 text-slate-400" />
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    Missing Optional Fields
                                </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {rider.missingOptionalFields.map((field, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                    >
                                        {field
                                            .replace(/_/g, " ")
                                            .replace(/\b\w/g, (letter) => letter.toUpperCase())}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            No optional profile fields are currently missing.
                        </p>
                    )}
                </div>
            </ProfileSectionCard>

            {hasNotificationPreferences && (
                <ProfileSectionCard title="Notification Preferences" className="xl:col-span-3">
                    <div className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-3">
                        {notificationEntries.map((entry) => (
                            <div
                                key={entry.label}
                                className="rounded-xl border border-slate-200 p-4 dark:border-slate-700"
                            >
                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                    {entry.label}
                                </p>
                                {entry.channels.length > 0 ? (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {entry.channels.map((channel) => (
                                            <span
                                                key={channel}
                                                className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                            >
                                                {formatEnumLabel(channel)}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                                        No channel enabled
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </ProfileSectionCard>
            )}

            <ProfileSectionCard title="Activity & Audit" className="xl:col-span-3">
                <div className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-5">
                    <ProfileDetail
                        label="User ID"
                        value={rider.id}
                        valueClassName="font-mono break-all"
                    />
                    <ProfileDetail label="Account Created" value={formatDate(rider.createdAt)} />
                    <ProfileDetail label="Profile Updated" value={formatDate(rider.updatedAt)} />
                    <ProfileDetail label="Last Login" value={formatDate(rider.lastLoginAt)} />
                    <ProfileDetail
                        label="Last Phone Number Change"
                        value={formatDate(rider.lastPhoneNumberChangeAt)}
                    />
                    {rider.deletedAt && (
                        <ProfileDetail label="Deleted At" value={formatDate(rider.deletedAt)} />
                    )}
                </div>
            </ProfileSectionCard>
        </div>
    );
};

// ============================================================================
// Bookings Tab
// ============================================================================

const BookingsTab = ({
    riderId,
    canManageBookings,
    canRefundBookings,
}: {
    riderId: string;
    canManageBookings: boolean;
    canRefundBookings: boolean;
}) => {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(0);
    const [cancelModal, setCancelModal] = useState<{ bookingId: string; code: string } | null>(null);
    const [refundModal, setRefundModal] = useState<{
        bookingId: string;
        code: string;
        totalPrice: number;
        currency: string;
    } | null>(null);
    const [cancelReason, setCancelReason] = useState("");
    const [refundReason, setRefundReason] = useState("");
    const [refundAmount, setRefundAmount] = useState("");
    const [cancelError, setCancelError] = useState("");
    const [refundError, setRefundError] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["riderBookings", riderId, page],
        queryFn: () => getRiderBookings(riderId, page),
        enabled: !!riderId,
    });

    const cancelMutation = useMutation({
        mutationFn: ({ bookingId, reason }: { bookingId: string; reason: string }) =>
            cancelBooking(bookingId, reason),
        onSuccess: (_data, { bookingId }) => {
            log.info("Booking cancelled", { bookingId, riderId });
            queryClient.invalidateQueries({ queryKey: ["riderBookings", riderId] });
            setCancelModal(null);
            setCancelReason("");
            setCancelError("");
        },
        onError: (err, { bookingId }) => {
            const msg = extractApiError(err);
            log.error("Failed to cancel booking", { bookingId, riderId, error: msg });
            setCancelError(msg);
        },
    });

    const refundMutation = useMutation({
        mutationFn: ({
            bookingId,
            reason,
            amount,
        }: {
            bookingId: string;
            reason: string;
            amount?: number;
        }) => refundBooking(bookingId, reason, amount),
        onSuccess: (_data, { bookingId, amount }) => {
            log.info("Booking refunded", { bookingId, riderId, amount });
            queryClient.invalidateQueries({ queryKey: ["riderBookings", riderId] });
            setRefundModal(null);
            setRefundReason("");
            setRefundAmount("");
            setRefundError("");
        },
        onError: (err, { bookingId }) => {
            const msg = extractApiError(err);
            log.error("Failed to refund booking", { bookingId, riderId, error: msg });
            setRefundError(msg);
        },
    });

    const bookingsData = data?.data;
    const showActionsColumn = canManageBookings || canRefundBookings;

    const handleCancelSubmit = () => {
        if (!cancelModal) return;
        cancelMutation.mutate({ bookingId: cancelModal.bookingId, reason: cancelReason });
    };

    const handleRefundSubmit = () => {
        if (!refundModal) return;
        const parsed = refundAmount ? parseFloat(refundAmount) : undefined;
        refundMutation.mutate({ bookingId: refundModal.bookingId, reason: refundReason, amount: parsed });
    };

    return (
        <>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Bookings</h2>
                        {bookingsData && (
                            <span className="text-sm text-slate-500 dark:text-slate-400">
                                {bookingsData.totalElements} total
                            </span>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <ErrorState message="Failed to load bookings." />
                ) : !bookingsData?.bookings?.length ? (
                    <EmptyState
                        icon={TicketIcon}
                        title="No bookings found"
                        subtitle="This rider hasn't made any bookings yet."
                    />
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                <thead className="bg-slate-50 dark:bg-slate-800/60">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Booking
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Route
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Driver
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Payment
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Date
                                        </th>
                                        {showActionsColumn ? (
                                            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Actions
                                            </th>
                                        ) : null}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                    {bookingsData.bookings.map((booking: RiderBooking) => (
                                        <tr
                                            key={booking.bookingId}
                                            className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                        >
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <div>
                                                    <p className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
                                                        {booking.bookingCode}
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {booking.numberOfSeats} seat
                                                        {booking.numberOfSeats > 1 ? "s" : ""}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="max-w-[200px]">
                                                    <div className="flex items-start gap-1.5">
                                                        <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                                        <p className="truncate text-xs text-slate-700 dark:text-slate-300">
                                                            {booking.pickupAddress}
                                                        </p>
                                                    </div>
                                                    <div className="mt-1 flex items-start gap-1.5">
                                                        <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
                                                        <p className="truncate text-xs text-slate-700 dark:text-slate-300">
                                                            {booking.dropoffAddress}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-900 dark:text-slate-100">
                                                    {booking.driverName}
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <StatusBadge status={booking.bookingStatus} />
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <StatusBadge status={booking.paymentStatus} />
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4 text-right">
                                                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                    {formatCurrency(booking.totalPrice, booking.currency)}
                                                </p>
                                            </td>
                                            <td className="whitespace-nowrap px-6 py-4">
                                                <p className="text-sm text-slate-700 dark:text-slate-300">
                                                    {formatShortDate(booking.bookedAt)}
                                                </p>
                                            </td>
                                            {showActionsColumn ? (
                                                <td className="whitespace-nowrap px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        {canManageBookings &&
                                                        booking.bookingStatus !== "CANCELLED" ? (
                                                            <button
                                                                onClick={() =>
                                                                    setCancelModal({
                                                                        bookingId: booking.bookingId,
                                                                        code: booking.bookingCode,
                                                                    })
                                                                }
                                                                className="rounded-lg border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                                            >
                                                                Cancel
                                                            </button>
                                                        ) : null}
                                                        {canRefundBookings && booking.paymentStatus === "PAID" ? (
                                                            <button
                                                                onClick={() =>
                                                                    setRefundModal({
                                                                        bookingId: booking.bookingId,
                                                                        code: booking.bookingCode,
                                                                        totalPrice: booking.totalPrice,
                                                                        currency: booking.currency,
                                                                    })
                                                                }
                                                                className="rounded-lg border border-amber-200 px-2.5 py-1 text-xs font-medium text-amber-600 transition hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/20"
                                                            >
                                                                Refund
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            ) : null}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            currentPage={bookingsData.currentPage}
                            totalPages={bookingsData.totalPages}
                            totalElements={bookingsData.totalElements}
                            onPageChange={setPage}
                        />
                    </>
                )}
            </div>

            {/* Cancel Booking Modal */}
            {cancelModal && (
                <ModalOverlay
                    onClose={() => {
                        setCancelModal(null);
                        setCancelError("");
                    }}
                >
                    <ModalCard>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            Cancel Booking
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                            Booking <span className="font-mono font-semibold">{cancelModal.code}</span>
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason
                                </label>
                                <textarea
                                    rows={3}
                                    value={cancelReason}
                                    onChange={(e) => {
                                        setCancelReason(e.target.value);
                                        if (cancelError) setCancelError("");
                                    }}
                                    placeholder="Reason for cancellation..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={cancelError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setCancelModal(null);
                                    setCancelError("");
                                }}
                                disabled={cancelMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleCancelSubmit}
                                disabled={cancelMutation.isPending}
                                className="flex-1 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
                            >
                                {cancelMutation.isPending ? "Cancelling..." : "Confirm Cancel"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Refund Booking Modal */}
            {refundModal && (
                <ModalOverlay
                    onClose={() => {
                        setRefundModal(null);
                        setRefundError("");
                    }}
                >
                    <ModalCard>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            Refund Booking
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                            Booking <span className="font-mono font-semibold">{refundModal.code}</span> &mdash;
                            Full amount:{" "}
                            <strong>
                                {formatCurrency(refundModal.totalPrice, refundModal.currency)}
                            </strong>
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Refund Amount{" "}
                                    <span className="text-xs text-slate-400">(leave blank for full refund)</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={refundAmount}
                                    onChange={(e) => {
                                        setRefundAmount(e.target.value);
                                        if (refundError) setRefundError("");
                                    }}
                                    placeholder={`Max: ${refundModal.totalPrice}`}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason
                                </label>
                                <textarea
                                    rows={3}
                                    value={refundReason}
                                    onChange={(e) => {
                                        setRefundReason(e.target.value);
                                        if (refundError) setRefundError("");
                                    }}
                                    placeholder="Reason for refund..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={refundError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setRefundModal(null);
                                    setRefundError("");
                                }}
                                disabled={refundMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleRefundSubmit}
                                disabled={refundMutation.isPending}
                                className="flex-1 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-amber-700 disabled:opacity-50"
                            >
                                {refundMutation.isPending ? "Processing..." : "Process Refund"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}
        </>
    );
};

// ============================================================================
// Trips Tab
// ============================================================================

const TripSummaryCards = ({ summary }: { summary: RiderTripSummary }) => (
    <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Completed</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {summary.completedTrips}
            </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Cancelled</p>
            <p className="mt-1 text-2xl font-bold text-rose-600 dark:text-rose-400">
                {summary.cancelledTrips}
            </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Total Spent</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
                {formatCurrency(summary.totalSpent)}
            </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Avg Rating Given</p>
            <div className="mt-1">
                <RatingStars rating={summary.avgRatingGivenToDrivers} />
            </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Avg Rating Received</p>
            <div className="mt-1">
                <RatingStars rating={summary.avgRatingReceivedFromDrivers} />
            </div>
        </div>
    </div>
);

const TripsTab = ({
    riderId,
    canManageTrips,
}: {
    riderId: string;
    canManageTrips: boolean;
}) => {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(0);
    const [cancelModal, setCancelModal] = useState<{ tripId: string } | null>(null);
    const [completeModal, setCompleteModal] = useState<{ tripId: string } | null>(null);
    const [cancelReason, setCancelReason] = useState("");
    const [completeReason, setCompleteReason] = useState("");
    const [completeFare, setCompleteFare] = useState("");
    const [cancelError, setCancelError] = useState("");
    const [completeError, setCompleteError] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["riderTrips", riderId, page],
        queryFn: () => getRiderTrips(riderId, page),
        enabled: !!riderId,
    });

    const cancelTripMutation = useMutation({
        mutationFn: ({ tripId, reason }: { tripId: string; reason: string }) =>
            cancelTrip(tripId, reason),
        onSuccess: (_data, { tripId }) => {
            log.info("Trip force-cancelled", { tripId, riderId });
            queryClient.invalidateQueries({ queryKey: ["riderTrips", riderId] });
            setCancelModal(null);
            setCancelReason("");
            setCancelError("");
        },
        onError: (err, { tripId }) => {
            const msg = extractApiError(err);
            log.error("Failed to cancel trip", { tripId, riderId, error: msg });
            setCancelError(msg);
        },
    });

    const completeTripMutation = useMutation({
        mutationFn: ({
            tripId,
            reason,
            finalFare,
        }: {
            tripId: string;
            reason: string;
            finalFare?: number;
        }) => forceCompleteTrip(tripId, reason, finalFare),
        onSuccess: (_data, { tripId, finalFare }) => {
            log.info("Trip force-completed", { tripId, riderId, finalFare });
            queryClient.invalidateQueries({ queryKey: ["riderTrips", riderId] });
            setCompleteModal(null);
            setCompleteReason("");
            setCompleteFare("");
            setCompleteError("");
        },
        onError: (err, { tripId }) => {
            const msg = extractApiError(err);
            log.error("Failed to force-complete trip", { tripId, riderId, error: msg });
            setCompleteError(msg);
        },
    });

    const tripsData = data?.data;

    const handleCancelSubmit = () => {
        if (!cancelModal) return;
        cancelTripMutation.mutate({ tripId: cancelModal.tripId, reason: cancelReason });
    };

    const handleCompleteSubmit = () => {
        if (!completeModal) return;
        const fare = completeFare ? parseFloat(completeFare) : undefined;
        completeTripMutation.mutate({ tripId: completeModal.tripId, reason: completeReason, finalFare: fare });
    };

    const activeStatuses = ["PENDING", "ACCEPTED", "IN_PROGRESS", "STARTED"];
    const showActionsColumn = canManageTrips;

    return (
        <>
            <div className="space-y-0">
                {tripsData?.summary && <TripSummaryCards summary={tripsData.summary} />}

                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                Trip History
                            </h2>
                            {tripsData && (
                                <span className="text-sm text-slate-500 dark:text-slate-400">
                                    {tripsData.totalElements} total
                                </span>
                            )}
                        </div>
                    </div>

                    {isLoading ? (
                        <LoadingSpinner />
                    ) : error ? (
                        <ErrorState message="Failed to load trips." />
                    ) : !tripsData?.trips?.length ? (
                        <EmptyState
                            icon={TruckIcon}
                            title="No trips found"
                            subtitle="This rider hasn't taken any trips yet."
                        />
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                    <thead className="bg-slate-50 dark:bg-slate-800/60">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Trip
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Route
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Driver
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Fare
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Details
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Rating
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Date
                                            </th>
                                            {showActionsColumn ? (
                                                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                    Actions
                                                </th>
                                            ) : null}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                        {tripsData.trips.map((trip: RiderTrip) => (
                                            <tr
                                                key={trip.tripId}
                                                className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                            >
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <p className="font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
                                                        {trip.tripId.slice(0, 8)}...
                                                    </p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {trip.numberOfSeats} seat
                                                        {trip.numberOfSeats > 1 ? "s" : ""}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="max-w-[200px]">
                                                        <div className="flex items-start gap-1.5">
                                                            <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" />
                                                            <p className="truncate text-xs text-slate-700 dark:text-slate-300">
                                                                {trip.pickupAddress}
                                                            </p>
                                                        </div>
                                                        <div className="mt-1 flex items-start gap-1.5">
                                                            <MapPinIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-rose-500" />
                                                            <p className="truncate text-xs text-slate-700 dark:text-slate-300">
                                                                {trip.dropoffAddress}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <p className="text-sm text-slate-900 dark:text-slate-100">
                                                        {trip.driverName}
                                                    </p>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <StatusBadge status={trip.status} />
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-right">
                                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                                        {formatCurrency(
                                                            trip.finalFare ?? trip.estimatedFare,
                                                            trip.currency
                                                        )}
                                                    </p>
                                                    {trip.finalFare !== null &&
                                                        trip.finalFare !== trip.estimatedFare && (
                                                            <p className="text-xs text-slate-400 line-through">
                                                                {formatCurrency(trip.estimatedFare, trip.currency)}
                                                            </p>
                                                        )}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <div className="text-xs text-slate-600 dark:text-slate-400">
                                                        {trip.distanceKm !== null && (
                                                            <p>{trip.distanceKm} km</p>
                                                        )}
                                                        {trip.durationMinutes !== null && (
                                                            <p>{trip.durationMinutes} min</p>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <div className="space-y-1">
                                                        {trip.driverRating && (
                                                            <div>
                                                                <p className="text-[10px] text-slate-400">
                                                                    To driver
                                                                </p>
                                                                <RatingStars
                                                                    rating={trip.driverRating.rating}
                                                                />
                                                            </div>
                                                        )}
                                                        {trip.passengerRating && (
                                                            <div>
                                                                <p className="text-[10px] text-slate-400">
                                                                    From driver
                                                                </p>
                                                                <RatingStars
                                                                    rating={trip.passengerRating.rating}
                                                                />
                                                            </div>
                                                        )}
                                                        {!trip.driverRating && !trip.passengerRating && (
                                                            <span className="text-xs text-slate-400">
                                                                {"\u2014"}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <p className="text-sm text-slate-700 dark:text-slate-300">
                                                        {formatShortDate(
                                                            trip.completedAt ||
                                                                trip.startedAt ||
                                                                trip.createdAt
                                                        )}
                                                    </p>
                                                </td>
                                                {showActionsColumn ? (
                                                    <td className="whitespace-nowrap px-6 py-4 text-right">
                                                        {activeStatuses.includes(trip.status) ? (
                                                            <div className="flex items-center justify-end gap-2">
                                                                <button
                                                                    onClick={() =>
                                                                        setCancelModal({ tripId: trip.tripId })
                                                                    }
                                                                    className="rounded-lg border border-rose-200 px-2.5 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={() =>
                                                                        setCompleteModal({ tripId: trip.tripId })
                                                                    }
                                                                    className="rounded-lg border border-emerald-200 px-2.5 py-1 text-xs font-medium text-emerald-600 transition hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                                                >
                                                                    Complete
                                                                </button>
                                                            </div>
                                                        ) : null}
                                                    </td>
                                                ) : null}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                currentPage={tripsData.currentPage}
                                totalPages={tripsData.totalPages}
                                totalElements={tripsData.totalElements}
                                onPageChange={setPage}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Cancel Trip Modal */}
            {cancelModal && (
                <ModalOverlay
                    onClose={() => {
                        setCancelModal(null);
                        setCancelError("");
                    }}
                >
                    <ModalCard>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            Force Cancel Trip
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                            Trip ID:{" "}
                            <span className="font-mono font-semibold">{cancelModal.tripId.slice(0, 8)}...</span>
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason
                                </label>
                                <textarea
                                    rows={3}
                                    value={cancelReason}
                                    onChange={(e) => {
                                        setCancelReason(e.target.value);
                                        if (cancelError) setCancelError("");
                                    }}
                                    placeholder="Reason for cancellation..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={cancelError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setCancelModal(null);
                                    setCancelError("");
                                }}
                                disabled={cancelTripMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleCancelSubmit}
                                disabled={cancelTripMutation.isPending}
                                className="flex-1 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
                            >
                                {cancelTripMutation.isPending ? "Cancelling..." : "Confirm Cancel"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Force Complete Trip Modal */}
            {completeModal && (
                <ModalOverlay
                    onClose={() => {
                        setCompleteModal(null);
                        setCompleteError("");
                    }}
                >
                    <ModalCard>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            Force Complete Trip
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                            Trip ID:{" "}
                            <span className="font-mono font-semibold">
                                {completeModal.tripId.slice(0, 8)}...
                            </span>
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Final Fare{" "}
                                    <span className="text-xs text-slate-400">(optional)</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={completeFare}
                                    onChange={(e) => {
                                        setCompleteFare(e.target.value);
                                        if (completeError) setCompleteError("");
                                    }}
                                    placeholder="Override fare amount..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason
                                </label>
                                <textarea
                                    rows={3}
                                    value={completeReason}
                                    onChange={(e) => {
                                        setCompleteReason(e.target.value);
                                        if (completeError) setCompleteError("");
                                    }}
                                    placeholder="Reason for force completion..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={completeError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setCompleteModal(null);
                                    setCompleteError("");
                                }}
                                disabled={completeTripMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleCompleteSubmit}
                                disabled={completeTripMutation.isPending}
                                className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {completeTripMutation.isPending ? "Completing..." : "Force Complete"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}
        </>
    );
};

// ============================================================================
// Wallet Tab
// ============================================================================

const WalletSummaryCards = ({
    balance,
    currency,
    walletStatus,
    summary,
}: {
    balance: number;
    currency: string;
    walletStatus: string;
    summary: RiderWalletSummary;
}) => (
    <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Balance</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
                {formatCurrency(balance, currency)}
            </p>
            <StatusBadge status={walletStatus} />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Lifetime Credits</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(summary.lifetimeCredits, currency)}
            </p>
            <p className="mt-1 text-xs text-slate-400">{summary.totalCreditTransactions} transactions</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Lifetime Debits</p>
            <p className="mt-1 text-2xl font-bold text-rose-600 dark:text-rose-400">
                {formatCurrency(summary.lifetimeDebits, currency)}
            </p>
            <p className="mt-1 text-xs text-slate-400">{summary.totalDebitTransactions} transactions</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Credit Txns</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
                {summary.totalCreditTransactions}
            </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Debit Txns</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">
                {summary.totalDebitTransactions}
            </p>
        </div>
    </div>
);

const WalletTab = ({
    riderId,
    canAdjustWallet,
    canFreezeWallet,
}: {
    riderId: string;
    canAdjustWallet: boolean;
    canFreezeWallet: boolean;
}) => {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(0);
    const [creditModal, setCreditModal] = useState(false);
    const [debitModal, setDebitModal] = useState(false);
    const [freezeModal, setFreezeModal] = useState(false);
    const [amount, setAmount] = useState("");
    const [reason, setReason] = useState("");
    const [freezeAction, setFreezeAction] = useState(true);
    const [walletActionError, setWalletActionError] = useState("");

    const { data, isLoading, error } = useQuery({
        queryKey: ["riderWallet", riderId, page],
        queryFn: () => getRiderWallet(riderId, page),
        enabled: !!riderId,
    });

    const walletData = data?.data;
    const walletId = walletData?.walletId;

    const creditMutation = useMutation({
        mutationFn: () => creditWallet(walletId!, { amount: parseFloat(amount), reason }),
        onSuccess: () => {
            log.info("Wallet credited", { walletId, riderId, amount });
            queryClient.invalidateQueries({ queryKey: ["riderWallet", riderId] });
            setCreditModal(false);
            setAmount("");
            setReason("");
            setWalletActionError("");
        },
        onError: (err) => {
            const msg = extractApiError(err);
            log.error("Failed to credit wallet", { walletId, riderId, error: msg });
            setWalletActionError(msg);
        },
    });

    const debitMutation = useMutation({
        mutationFn: () => debitWallet(walletId!, { amount: parseFloat(amount), reason }),
        onSuccess: () => {
            log.info("Wallet debited", { walletId, riderId, amount });
            queryClient.invalidateQueries({ queryKey: ["riderWallet", riderId] });
            setDebitModal(false);
            setAmount("");
            setReason("");
            setWalletActionError("");
        },
        onError: (err) => {
            const msg = extractApiError(err);
            log.error("Failed to debit wallet", { walletId, riderId, error: msg });
            setWalletActionError(msg);
        },
    });

    const freezeMutation = useMutation({
        mutationFn: () => freezeWallet(walletId!, { freeze: freezeAction, reason }),
        onSuccess: () => {
            log.info("Wallet freeze toggled", { walletId, riderId, freeze: freezeAction });
            queryClient.invalidateQueries({ queryKey: ["riderWallet", riderId] });
            setFreezeModal(false);
            setReason("");
            setWalletActionError("");
        },
        onError: (err) => {
            const msg = extractApiError(err);
            log.error("Failed to freeze/unfreeze wallet", { walletId, riderId, error: msg });
            setWalletActionError(msg);
        },
    });

    return (
        <>
            <div className="space-y-0">
                {walletData?.summary && (
                    <WalletSummaryCards
                        balance={walletData.balance}
                        currency={walletData.currency}
                        walletStatus={walletData.walletStatus}
                        summary={walletData.summary}
                    />
                )}

                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                    <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                                    Transactions
                                </h2>
                                {walletData && (
                                    <span className="text-sm text-slate-500 dark:text-slate-400">
                                        {walletData.totalElements} total
                                    </span>
                                )}
                            </div>
                            {walletId && (canAdjustWallet || canFreezeWallet) ? (
                                <div className="flex items-center gap-2">
                                    {canAdjustWallet ? (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setAmount("");
                                                    setReason("");
                                                    setCreditModal(true);
                                                }}
                                                className="flex items-center gap-1.5 rounded-lg border border-emerald-200 px-3 py-1.5 text-xs font-medium text-emerald-700 transition hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                            >
                                                <ArrowDownIcon className="h-3.5 w-3.5" />
                                                Credit
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setAmount("");
                                                    setReason("");
                                                    setDebitModal(true);
                                                }}
                                                className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
                                            >
                                                <ArrowUpIcon className="h-3.5 w-3.5" />
                                                Debit
                                            </button>
                                        </>
                                    ) : null}
                                    {canFreezeWallet ? (
                                        <button
                                            onClick={() => {
                                                setFreezeAction(walletData?.walletStatus !== "FROZEN");
                                                setReason("");
                                                setFreezeModal(true);
                                            }}
                                            className="flex items-center gap-1.5 rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                        >
                                            <LockClosedIcon className="h-3.5 w-3.5" />
                                            {walletData?.walletStatus === "FROZEN" ? "Unfreeze" : "Freeze"}
                                        </button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {isLoading ? (
                        <LoadingSpinner />
                    ) : error ? (
                        <ErrorState message="Failed to load wallet." />
                    ) : !walletData?.transactions?.length ? (
                        <EmptyState
                            icon={WalletIcon}
                            title="No transactions"
                            subtitle="This rider has no wallet transactions yet."
                        />
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                                    <thead className="bg-slate-50 dark:bg-slate-800/60">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Direction
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Type
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Amount
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Description
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Reference
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                                Date
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                                        {walletData.transactions.map((txn: RiderWalletTransaction) => (
                                            <tr
                                                key={txn.transactionId}
                                                className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                            >
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        {txn.direction === "CREDIT" ? (
                                                            <ArrowDownIcon className="h-4 w-4 text-emerald-500" />
                                                        ) : (
                                                            <ArrowUpIcon className="h-4 w-4 text-rose-500" />
                                                        )}
                                                        <StatusBadge status={txn.direction} />
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <p className="text-sm text-slate-900 dark:text-slate-100">
                                                        {txn.type?.replace(/_/g, " ")}
                                                    </p>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-right">
                                                    <p
                                                        className={`text-sm font-semibold ${txn.direction === "CREDIT" ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}
                                                    >
                                                        {txn.direction === "CREDIT" ? "+" : "-"}
                                                        {formatCurrency(txn.amount, txn.currency)}
                                                    </p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="max-w-[200px] truncate text-sm text-slate-700 dark:text-slate-300">
                                                        {txn.description}
                                                    </p>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <p className="font-mono text-xs text-slate-500 dark:text-slate-400">
                                                        {txn.reference
                                                            ? `${txn.reference.slice(0, 12)}...`
                                                            : "\u2014"}
                                                    </p>
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <StatusBadge status={txn.status} />
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <p className="text-sm text-slate-700 dark:text-slate-300">
                                                        {formatShortDate(txn.createdAt)}
                                                    </p>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                currentPage={walletData.currentPage}
                                totalPages={walletData.totalPages}
                                totalElements={walletData.totalElements}
                                onPageChange={setPage}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Credit Wallet Modal */}
            {creditModal && (
                <ModalOverlay
                    onClose={() => {
                        setCreditModal(false);
                        setWalletActionError("");
                    }}
                >
                    <ModalCard>
                        <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                                <ArrowDownIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                Credit Wallet
                            </h3>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Amount (NGN)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={amount}
                                    onChange={(e) => {
                                        setAmount(e.target.value);
                                        if (walletActionError) setWalletActionError("");
                                    }}
                                    placeholder="0.00"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason
                                </label>
                                <textarea
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => {
                                        setReason(e.target.value);
                                        if (walletActionError) setWalletActionError("");
                                    }}
                                    placeholder="Reason for credit..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={walletActionError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setCreditModal(false);
                                    setWalletActionError("");
                                }}
                                disabled={creditMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => creditMutation.mutate()}
                                disabled={creditMutation.isPending || !amount || !reason}
                                className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
                            >
                                {creditMutation.isPending ? "Processing..." : "Credit Wallet"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Debit Wallet Modal */}
            {debitModal && (
                <ModalOverlay
                    onClose={() => {
                        setDebitModal(false);
                        setWalletActionError("");
                    }}
                >
                    <ModalCard>
                        <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/30">
                                <ArrowUpIcon className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                Debit Wallet
                            </h3>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Amount (NGN)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={amount}
                                    onChange={(e) => {
                                        setAmount(e.target.value);
                                        if (walletActionError) setWalletActionError("");
                                    }}
                                    placeholder="0.00"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason
                                </label>
                                <textarea
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => {
                                        setReason(e.target.value);
                                        if (walletActionError) setWalletActionError("");
                                    }}
                                    placeholder="Reason for debit..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={walletActionError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setDebitModal(false);
                                    setWalletActionError("");
                                }}
                                disabled={debitMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => debitMutation.mutate()}
                                disabled={debitMutation.isPending || !amount || !reason}
                                className="flex-1 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
                            >
                                {debitMutation.isPending ? "Processing..." : "Debit Wallet"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Freeze Wallet Modal */}
            {freezeModal && (
                <ModalOverlay
                    onClose={() => {
                        setFreezeModal(false);
                        setWalletActionError("");
                    }}
                >
                    <ModalCard>
                        <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                                <LockClosedIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                {freezeAction ? "Freeze Wallet" : "Unfreeze Wallet"}
                            </h3>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason
                                </label>
                                <textarea
                                    rows={3}
                                    value={reason}
                                    onChange={(e) => {
                                        setReason(e.target.value);
                                        if (walletActionError) setWalletActionError("");
                                    }}
                                    placeholder={
                                        freezeAction
                                            ? "Reason for freezing wallet..."
                                            : "Reason for unfreezing wallet..."
                                    }
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={walletActionError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setFreezeModal(false);
                                    setWalletActionError("");
                                }}
                                disabled={freezeMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => freezeMutation.mutate()}
                                disabled={freezeMutation.isPending || !reason}
                                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-50 ${freezeAction ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                            >
                                {freezeMutation.isPending
                                    ? "Processing..."
                                    : freezeAction
                                      ? "Freeze Wallet"
                                      : "Unfreeze Wallet"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}
        </>
    );
};

// ============================================================================
// Fraud & Safety Tab
// ============================================================================

const FraudTab = ({ userId }: { userId: string }) => {
    const { data: riskData, isLoading: riskLoading, error: riskError } = useQuery({
        queryKey: ["riderRiskScore", userId],
        queryFn: () => getUserRiskScore(userId),
        enabled: !!userId,
    });

    const riskScore = riskData?.data;

    const riskColor =
        riskScore?.riskLevel === "LOW"
            ? "text-emerald-600 dark:text-emerald-400"
            : riskScore?.riskLevel === "MEDIUM"
              ? "text-amber-600 dark:text-amber-400"
              : "text-rose-600 dark:text-rose-400";

    const riskBg =
        riskScore?.riskLevel === "LOW"
            ? "bg-emerald-100 dark:bg-emerald-900/30"
            : riskScore?.riskLevel === "MEDIUM"
              ? "bg-amber-100 dark:bg-amber-900/30"
              : "bg-rose-100 dark:bg-rose-900/30";

    return (
        <div className="space-y-6">
            {/* Risk Score Card */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="border-b border-slate-200 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Risk Assessment
                    </h2>
                </div>
                <div className="p-6">
                    {riskLoading ? (
                        <LoadingSpinner />
                    ) : riskError ? (
                        <ErrorState message="Failed to load risk score." />
                    ) : riskScore ? (
                        <div className="space-y-6">
                            <div className="flex items-center gap-6">
                                <div
                                    className={`flex h-24 w-24 flex-col items-center justify-center rounded-full ${riskBg}`}
                                >
                                    <span className={`text-3xl font-bold ${riskColor}`}>
                                        {riskScore.overallScore}
                                    </span>
                                    <span className={`text-xs font-semibold ${riskColor}`}>
                                        {riskScore.riskLevel}
                                    </span>
                                </div>
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Overall Risk Score
                                    </p>
                                    <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                                        <div
                                            className={`h-full rounded-full transition-all ${riskScore.overallScore < 40 ? "bg-emerald-500" : riskScore.overallScore < 70 ? "bg-amber-500" : "bg-rose-500"}`}
                                            style={{ width: `${riskScore.overallScore}%` }}
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                        Score: {riskScore.overallScore} / 100
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
                                        <span>{riskScore.totalFraudAlerts} fraud alert{riskScore.totalFraudAlerts !== 1 ? "s" : ""}</span>
                                        <span>{riskScore.totalInvestigationCases} investigation case{riskScore.totalInvestigationCases !== 1 ? "s" : ""}</span>
                                        <span>{riskScore.pendingVerifications} pending verification{riskScore.pendingVerifications !== 1 ? "s" : ""}</span>
                                    </div>
                                </div>
                            </div>

                            {riskScore.riskFactors && Object.keys(riskScore.riskFactors).length > 0 && (
                                <div>
                                    <p className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
                                        Risk Factors
                                    </p>
                                    <div className="space-y-2">
                                        {Object.entries(riskScore.riskFactors).map(([key, factor]: [string, any]) => (
                                            <div
                                                key={key}
                                                className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 dark:border-slate-700"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <ExclamationTriangleIcon className="h-4 w-4 text-amber-500" />
                                                    <div>
                                                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                            {factor.name || key.replace(/_/g, " ")}
                                                        </p>
                                                        {factor.description && (
                                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                                {factor.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <span className={`text-sm font-bold ${factor.score > 60 ? "text-rose-600 dark:text-rose-400" : factor.score > 30 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                                                    {factor.score}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <EmptyState
                            icon={ShieldExclamationIcon}
                            title="No risk data"
                            subtitle="Risk score not available for this rider."
                        />
                    )}
                </div>
            </div>

        </div>
    );
};

// ============================================================================
// Main Component
// ============================================================================

export const RiderDetailsRoute = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { hasAnyPermission, hasPermission } = usePermissions();
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabKey>("profile");

    // Page-level flash notification
    const [flashMessage, setFlashMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

    // Status action modal
    const [statusModal, setStatusModal] = useState<{
        action: "activate" | "reactivate" | "deactivate" | "suspend";
        label: string;
    } | null>(null);
    const [statusReason, setStatusReason] = useState("");
    const [statusError, setStatusError] = useState("");

    // Permanent ban modal
    const [banModal, setBanModal] = useState(false);
    const [banReason, setBanReason] = useState("");
    const [banError, setBanError] = useState("");

    // Push notification modal
    const [pushModal, setPushModal] = useState(false);
    const [pushTitle, setPushTitle] = useState("Message from Ride2Five Admin");
    const [pushMessage, setPushMessage] = useState("");
    const [pushError, setPushError] = useState("");

    const {
        presence,
        connectionState: presenceConnectionState,
        error: presenceError,
        lastUpdatedAt: presenceLastUpdatedAt,
        refresh: refreshPresence,
    } = useUserPresenceLookup(userId ?? null);
    const canViewBookings = hasPermission(SECTION_PERMISSIONS.RIDER_BOOKINGS[0]);
    const canViewTrips = hasPermission(SECTION_PERMISSIONS.RIDER_TRIPS[0]);
    const canViewWallet = hasPermission(SECTION_PERMISSIONS.RIDER_WALLET[0]);
    const canViewFraud = hasPermission(SECTION_PERMISSIONS.FRAUD[0]);
    const canModerateRider = hasPermission(SECTION_PERMISSIONS.USERS_MODERATE[0]);
    const canSendPush = hasPermission(SECTION_PERMISSIONS.PUSH_NOTIFICATIONS[0]);
    const canManageBookings = hasPermission(SECTION_PERMISSIONS.RIDE_BOOKINGS_MANAGE[0]);
    const canRefundBookings = hasPermission(SECTION_PERMISSIONS.RIDE_BOOKINGS_REFUND[0]);
    const canManageTrips = hasPermission(SECTION_PERMISSIONS.TRIPS_MANAGE[0]);
    const canAdjustWallet = hasPermission(SECTION_PERMISSIONS.WALLET_ADJUST[0]);
    const canFreezeWallet = hasPermission(SECTION_PERMISSIONS.WALLET_FREEZE[0]);
    const visibleTabs = TABS.filter((tab) =>
        !tab.permissions ? true : hasAnyPermission(tab.permissions)
    );

    const { data, isLoading, error } = useQuery({
        queryKey: ["riderDetails", userId],
        queryFn: () => getRiderDetails(userId!),
        enabled: !!userId,
    });

    // Auto-dismiss flash after 5 seconds
    useEffect(() => {
        if (!flashMessage) return;
        const timer = setTimeout(() => setFlashMessage(null), 5000);
        return () => clearTimeout(timer);
    }, [flashMessage]);

    useEffect(() => {
        if (!visibleTabs.some((tab) => tab.key === activeTab)) {
            setActiveTab(visibleTabs[0]?.key ?? "profile");
        }
    }, [activeTab, visibleTabs]);

    const statusMutation = useMutation({
        mutationFn: ({
            action,
            reason,
        }: {
            action: "activate" | "reactivate" | "deactivate" | "suspend";
            reason?: string;
        }) => {
            if (action === "activate") return activateRider(userId!, reason);
            if (action === "reactivate") return reactivateRider(userId!, reason);
            if (action === "deactivate") return deactivateRider(userId!, reason);
            return suspendRider(userId!, reason);
        },
        onSuccess: (response, { action }) => {
            log.info("Rider status updated", { userId, action });
            queryClient.invalidateQueries({ queryKey: ["riderDetails", userId] });
            queryClient.invalidateQueries({ queryKey: ["riders"] });
            setStatusModal(null);
            setStatusReason("");
            setStatusError("");
            setFlashMessage({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        },
        onError: (err, { action }) => {
            const msg = extractApiError(err);
            log.error("Failed to update rider status", { userId, action, error: msg });
            setStatusError(msg);
        },
    });

    const banMutation = useMutation({
        mutationFn: (reason?: string) => banRider(userId!, reason),
        onSuccess: (response) => {
            log.info("Rider permanently banned", { userId });
            queryClient.invalidateQueries({ queryKey: ["riderDetails", userId] });
            queryClient.invalidateQueries({ queryKey: ["riders"] });
            setBanModal(false);
            setBanReason("");
            setBanError("");
            setFlashMessage({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        },
        onError: (err) => {
            const msg = extractApiError(err);
            log.error("Failed to ban rider", { userId, error: msg });
            setBanError(msg);
        },
    });

    const pushMutation = useMutation({
        mutationFn: () =>
            sendPushNotificationToUser({
                userId: userId!,
                title: pushTitle.trim(),
                body: pushMessage.trim(),
                type: "SYSTEM_ALERT",
                data: {
                    screen: "rider_details",
                    source: "admin_panel",
                },
                highPriority: true,
            }),
        onSuccess: (response) => {
            log.info("Push notification sent to rider", { userId });
            setPushModal(false);
            setPushTitle("Message from Ride2Five Admin");
            setPushMessage("");
            setPushError("");
            setFlashMessage({
                type: "success",
                text: buildApprovalSubmissionFeedback(response),
            });
        },
        onError: (err) => {
            const msg = extractApiError(err);
            log.error("Failed to send push notification", { userId, error: msg });
            setPushError(msg);
        },
    });

    if (isLoading) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent" />
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Loading rider details...
                    </p>
                </div>
            </div>
        );
    }

    if (error || !data?.data) {
        return (
            <div className="flex h-96 items-center justify-center">
                <div className="text-center">
                    <p className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                        Failed to load rider details
                    </p>
                    <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
                        Please try again later.
                    </p>
                    <button
                        onClick={() => navigate("/riders")}
                        className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600"
                    >
                        Back to Riders
                    </button>
                </div>
            </div>
        );
    }

    const rider: RiderDetails = data.data;

    const statusActions = () => {
        if (!canModerateRider) {
            return [];
        }
        const actions = [];
        if (rider.status === "ACTIVE" || rider.status === "INACTIVE") {
            actions.push({ action: "suspend" as const, label: "Suspend", color: "amber" });
        }
        if (rider.status === "SUSPENDED") {
            actions.push({ action: "reactivate" as const, label: "Unsuspend", color: "emerald" });
        }
        return actions;
    };

    return (
        <div className="space-y-6">
            {/* Flash notification */}
            {flashMessage && (
                <div
                    className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium ${
                        flashMessage.type === "success"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400"
                            : "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-400"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        {flashMessage.type === "success" ? (
                            <CheckCircleIcon className="h-4 w-4 shrink-0" />
                        ) : (
                            <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                        )}
                        <span>{flashMessage.text}</span>
                    </div>
                    <button
                        onClick={() => setFlashMessage(null)}
                        className="ml-4 rounded p-0.5 opacity-60 transition hover:opacity-100"
                        aria-label="Dismiss"
                    >
                        <XCircleIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate("/riders")}
                    className="flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                    Back to Riders
                </button>
                <div className="flex items-center gap-2">
                    {canSendPush ? (
                        <button
                            onClick={() => {
                                setPushModal(true);
                                setPushTitle("Message from Ride2Five Admin");
                                setPushMessage("");
                                setPushError("");
                            }}
                            className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                        >
                            <PaperAirplaneIcon className="h-4 w-4" />
                            Send Push Notification
                        </button>
                    ) : null}
                    {statusActions().map(({ action, label, color }) => (
                        <button
                            key={action}
                            onClick={() => {
                                setStatusModal({ action, label });
                                setStatusReason("");
                            }}
                            className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition ${
                                color === "emerald"
                                    ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
                                    : color === "amber"
                                      ? "border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/20"
                                      : "border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                            }`}
                        >
                            {color === "emerald" ? (
                                <PowerIcon className="h-4 w-4" />
                            ) : color === "amber" ? (
                                <NoSymbolIcon className="h-4 w-4" />
                            ) : (
                                <PowerIcon className="h-4 w-4" />
                            )}
                            {label}
                        </button>
                    ))}
                    {canModerateRider && rider.status !== "BANNED" && !rider.deleted ? (
                        <button
                            onClick={() => { setBanModal(true); setBanReason(""); setBanError(""); }}
                            className="flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2 text-sm font-medium text-rose-600 transition hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-900/20"
                        >
                            <TrashIcon className="h-4 w-4" />
                            Ban (Permanent)
                        </button>
                    ) : null}
                </div>
            </div>

            {/* Profile Header Card */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-8 py-6">
                    <div className="flex items-center gap-6">
                        {rider.profilePicture ? (
                            <img
                                src={rider.profilePicture}
                                alt={`${rider.firstName} ${rider.lastName}`}
                                className="h-20 w-20 cursor-zoom-in rounded-full object-cover ring-4 ring-white/20 transition-transform hover:scale-105"
                                onClick={() => setSelectedImage(rider.profilePicture)}
                            />
                        ) : (
                            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 text-2xl font-bold text-white backdrop-blur-sm">
                                {rider.firstName[0]}
                                {rider.lastName[0]}
                            </div>
                        )}
                        <div className="flex-1">
                            <div className="mb-2 flex items-center gap-3">
                                <h1 className="text-2xl font-bold text-white">
                                    {rider.firstName} {rider.lastName}
                                </h1>
                                <StatusBadge status={rider.status} />
                            </div>
                            <p className="text-sm text-primary-100">User ID: {rider.id}</p>
                        </div>
                    </div>
                </div>

                {/* Profile Stats */}
                <div className="grid grid-cols-1 gap-6 border-t border-slate-200 p-8 dark:border-slate-800 md:grid-cols-3">
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <UserCircleIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                Profile Completion
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                                <div
                                    className="h-full rounded-full bg-primary-500 transition-all"
                                    style={{ width: `${rider.profileCompletionPercentage}%` }}
                                />
                            </div>
                            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {rider.profileCompletionPercentage}%
                            </span>
                        </div>
                    </div>
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <ClockIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                Last Login
                            </span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {formatDate(rider.lastLoginAt)}
                        </p>
                    </div>
                    <div>
                        <div className="mb-2 flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-slate-400" />
                            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                                Joined
                            </span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {formatDate(rider.createdAt)}
                        </p>
                    </div>
                </div>
            </div>

            <PresencePanel
                presence={presence}
                connectionState={presenceConnectionState}
                error={presenceError}
                lastUpdatedAt={presenceLastUpdatedAt}
                onRefresh={refreshPresence}
            />

            {/* Tabs */}
            <div className="border-b border-slate-200 dark:border-slate-800">
                <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Tabs">
                    {visibleTabs.map(({ key, label, icon: Icon }) => (
                        <button
                            key={key}
                            onClick={() => setActiveTab(key)}
                            className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-1 pb-3 pt-1 text-sm font-medium transition ${
                                activeTab === key
                                    ? "border-primary-500 text-primary-600 dark:text-primary-400"
                                    : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                            }`}
                        >
                            <Icon className="h-5 w-5" />
                            {label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === "profile" && <ProfileTab rider={rider} />}
            {activeTab === "bookings" && userId && canViewBookings ? (
                <BookingsTab
                    riderId={userId}
                    canManageBookings={canManageBookings}
                    canRefundBookings={canRefundBookings}
                />
            ) : null}
            {activeTab === "trips" && userId && canViewTrips ? (
                <TripsTab riderId={userId} canManageTrips={canManageTrips} />
            ) : null}
            {activeTab === "wallet" && userId && canViewWallet ? (
                <WalletTab
                    riderId={userId}
                    canAdjustWallet={canAdjustWallet}
                    canFreezeWallet={canFreezeWallet}
                />
            ) : null}
            {activeTab === "fraud" && userId && canViewFraud ? <FraudTab userId={userId} /> : null}

            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                    onClick={() => setSelectedImage(null)}
                >
                    <button
                        onClick={() => setSelectedImage(null)}
                        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white backdrop-blur-sm transition hover:bg-white/20"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                    <div className="relative max-h-[90vh] max-w-[90vw]">
                        <img
                            src={selectedImage}
                            alt={`${rider.firstName} ${rider.lastName}`}
                            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                            onError={(event) => {
                                const target = event.target as HTMLImageElement;
                                target.src = "";
                                target.alt = "Failed to load image";
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Status Change Modal */}
            {statusModal && (
                <ModalOverlay
                    onClose={() => {
                        setStatusModal(null);
                        setStatusError("");
                    }}
                >
                    <ModalCard>
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                            {statusModal.label} Rider
                        </h3>
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                            {rider.firstName} {rider.lastName}
                        </p>
                        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                            This change will be submitted to the super admin approval queue before
                            it takes effect.
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason <span className="text-xs text-slate-400">(optional)</span>
                                </label>
                                <textarea
                                    rows={3}
                                    value={statusReason}
                                    onChange={(e) => {
                                        setStatusReason(e.target.value);
                                        if (statusError) setStatusError("");
                                    }}
                                    placeholder={`Reason for ${statusModal.label.toLowerCase()}...`}
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={statusError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setStatusModal(null);
                                    setStatusError("");
                                }}
                                disabled={statusMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() =>
                                    statusMutation.mutate({
                                        action: statusModal.action,
                                        reason: statusReason || undefined,
                                    })
                                }
                                disabled={statusMutation.isPending}
                                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-50 ${
                                    statusModal.action === "suspend"
                                        ? "bg-amber-600 hover:bg-amber-700"
                                        : statusModal.action === "deactivate"
                                          ? "bg-slate-600 hover:bg-slate-700"
                                          : "bg-emerald-600 hover:bg-emerald-700"
                                }`}
                            >
                                {statusMutation.isPending
                                    ? "Submitting..."
                                    : `Submit ${statusModal.label} Request`}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Permanent Ban Modal */}
            {banModal && (
                <ModalOverlay
                    onClose={() => {
                        setBanModal(false);
                        setBanError("");
                    }}
                >
                    <ModalCard>
                        <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/30">
                                <TrashIcon className="h-5 w-5 text-rose-600 dark:text-rose-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                Submit Rider Ban Request
                            </h3>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                            Are you sure you want to permanently ban{" "}
                            <strong>
                                {rider.firstName} {rider.lastName}
                            </strong>
                            ? This request will go to the super admin approval queue first. Once
                            approved, the account cannot be reactivated and the email/phone cannot
                            be reused.
                        </p>
                        <div className="mt-4 space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Reason <span className="text-xs text-slate-400">(optional)</span>
                                </label>
                                <textarea
                                    rows={3}
                                    value={banReason}
                                    onChange={(e) => {
                                        setBanReason(e.target.value);
                                        if (banError) setBanError("");
                                    }}
                                    placeholder="Reason for permanent ban..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <InlineError message={banError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setBanModal(false);
                                    setBanError("");
                                }}
                                disabled={banMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => banMutation.mutate(banReason || undefined)}
                                disabled={banMutation.isPending}
                                className="flex-1 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-50"
                            >
                                {banMutation.isPending
                                    ? "Submitting..."
                                    : "Submit Ban Request"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}

            {/* Send Push Notification Modal */}
            {pushModal && (
                <ModalOverlay
                    onClose={() => {
                        setPushModal(false);
                        setPushTitle("Message from Ride2Five Admin");
                        setPushMessage("");
                        setPushError("");
                    }}
                >
                    <ModalCard>
                        <div className="mb-4 flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                                <PaperAirplaneIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                                    Submit Push Notification
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    To: {rider.firstName} {rider.lastName} &middot; {rider.id}
                                </p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Title
                                </label>
                                <input
                                    type="text"
                                    value={pushTitle}
                                    onChange={(e) => {
                                        setPushTitle(e.target.value);
                                        if (pushError) setPushError("");
                                    }}
                                    placeholder="Push notification title"
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                            </div>
                            <div>
                                <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                                    Message
                                </label>
                                <textarea
                                    rows={4}
                                    value={pushMessage}
                                    onChange={(e) => {
                                        setPushMessage(e.target.value);
                                        if (pushError) setPushError("");
                                    }}
                                    placeholder="Type your message..."
                                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                                />
                                <p className="mt-1 text-right text-xs text-slate-400">
                                    {pushMessage.length} chars
                                </p>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    The rider will receive this targeted push notification after it
                                    is approved.
                                </p>
                            </div>
                            <InlineError message={pushError} />
                        </div>
                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => {
                                    setPushModal(false);
                                    setPushTitle("Message from Ride2Five Admin");
                                    setPushMessage("");
                                    setPushError("");
                                }}
                                disabled={pushMutation.isPending}
                                className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => pushMutation.mutate()}
                                disabled={
                                    pushMutation.isPending ||
                                    !pushTitle.trim() ||
                                    !pushMessage.trim()
                                }
                                className="flex-1 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-primary-600 disabled:opacity-50"
                            >
                                {pushMutation.isPending
                                    ? "Submitting..."
                                    : "Submit Push Request"}
                            </button>
                        </div>
                    </ModalCard>
                </ModalOverlay>
            )}
        </div>
    );
};
